import React, { useState, useEffect, useRef, useContext, ReactNode } from "react";
import { getCounties, getZipCodes, getCountyPostalCodes } from "requests/region";
import { getServiceRegionsDetailed } from "requests/serviceRegions";
import { CountyDto, ZipCodeDto, ServiceRegionDto, InspectorServiceRegionResponse, CountyPostalDto } from "interfaces/Mueller.Business.Models";
import { mapArrayOfPolygonsV2, MapPolygonProps } from "../Shared/MapHelpers/PolygonHelpers";
import { TooltipContext, TooltipProviderValue } from "../Shared/TooltipProvider";
import { ServiceTypeEnum } from "typeConstants/ServiceTypeEnum";
import { backgroundColor, fontColor, MCard, assignmentToolColors } from "components/Shared";
import { BoundaryType } from "components/InspectorRegionManager";

const combineSets = <T, >(set: Set<T>, collection: T[]) => {
  let setCopy = new Set(set);
  collection.forEach(c => setCopy.add(c));
  return setCopy;
};

const fullSecondaryOpacity = 0.9;
const fullOpacity = 0.6;
const partialOpacity = 0.2;
const fullCoverageCounty = fontColor.primary;
const partialCoverageCounty = fontColor.primary;
const secondaryCoveragePrimaryColor = assignmentToolColors.countyBorderColor;
const fullSecondaryCoverageCounty = secondaryCoveragePrimaryColor;
const noCoverageCounty = fontColor.disabled;
const countyBorderColor = assignmentToolColors.countyBorderColor;
const zipBorderColor = assignmentToolColors.zipBorderColor;
const fullCoverageZip = backgroundColor.button;
const secondaryCoverageZip = secondaryCoveragePrimaryColor;

const countyBorderSize = 1;
const countyBorderSizeZipCoverageOverride = 4;

const zipBorderSize = 1;

type CoverageLevel = "noCoverage" | "partialCoverage" | "fullCoverage" | "fullSecondaryCoverage" | "partialSecondaryCoverage";

interface PolygonFills {
  borderColor: string,
  fillColor: string,
  fillOpacity: number,
  borderSize: number,
};

const mapColors = {
  zip: {
    noCoverage: {
      borderColor: zipBorderColor,
      fillColor: fullCoverageZip,
      fillOpacity: 0,
      borderSize: zipBorderSize,
    },
    fullCoverage: {
      borderColor: zipBorderColor,
      fillColor: fullCoverageZip,
      fillOpacity: fullOpacity,
      borderSize: zipBorderSize,
    },
    fullSecondaryCoverage: {
      borderColor: zipBorderColor,
      fillColor: secondaryCoverageZip,
      fillOpacity: fullSecondaryOpacity,
      borderSize: zipBorderSize,
    },
  },
  county: {
    noCoverage: {
      borderColor: countyBorderColor,
      fillColor: noCoverageCounty,
      fillOpacity: 0,
      borderSize: countyBorderSize,
    },
    // partial primary coverage and partial secondary coverage display the same colors
    partialCoverage: {
      borderColor: countyBorderColor,
      fillColor: partialCoverageCounty,
      fillOpacity: partialOpacity,
      borderSize: countyBorderSize,
    },
    fullCoverage: {
      borderColor: countyBorderColor,
      fillColor: fullCoverageCounty,
      fillOpacity: fullOpacity,
      borderSize: countyBorderSize,
    },
    fullSecondaryCoverage: {
      borderColor: countyBorderColor,
      fillColor: fullSecondaryCoverageCounty,
      fillOpacity: fullSecondaryOpacity,
      borderSize: countyBorderSize,
    },
  } as Record<CoverageLevel, PolygonFills>,
};

interface PolyMeta {
  polyType: BoundaryType,
  state: string,
  mouseOver: ReactNode,
};

interface FieldRepCoverageData {
  id: number,
  name: string,
  serviceRegions: InspectorServiceRegionResponse[],
  isSecondary?: boolean,
};

interface FRCoverageHookParams {
  fieldReps: FieldRepCoverageData[],
  selectedStates: string[],
  showCoverage: boolean,
  showZipCoverage: boolean,
  showTooltips: boolean,
  forceUpdate: any, // just change the value of this to cause an update to cached polys, use numbers, dates, whatever
};

const useFRCoverage = ({
  fieldReps,
  selectedStates,
  showCoverage,
  showZipCoverage,
  showTooltips,
  forceUpdate = null,
}: FRCoverageHookParams) => {
  const mounted = useRef(true);
  // loading vars (we cant just check that the array has entries because we will lazy load these
  const [isLoadingCountyData, setIsLoadingCountyData] = useState(false);
  const [isLoadingZipData, setIsLoadingZipData] = useState(false);
  const [isLoadingMSAData, setIsLoadingMSAData] = useState(false);
  const [isLoadingCountyPostalData, setIsLoadingCountyPostalData] = useState(false);

  // data state vars
  const [msaData, setMsaData] = useState<ServiceRegionDto[]>([]);
  const [countyData, setCountyData] = useState<CountyDto[]>([]);
  const [zipData, setZipData] = useState<ZipCodeDto[]>([]);
  const [countyPostalLinks, setCountyPostalLinks] = useState<CountyPostalDto[]>([]);
  const [loadedStates, setLoadedStates] = useState<Set<string>>(new Set());
  
  const [cachedBoundaryPolys, setCachedBoundaryPolys] = useState<MapPolygonProps<PolyMeta>[]>(null);
  const [visibleBoundaryPolys, setVisibleBoundaryPolys] = useState<MapPolygonProps<PolyMeta>[]>([]);

  const tooltipContext = useContext<TooltipProviderValue>(TooltipContext);
  
  useEffect(() => () => {
    mounted.current = false;
    tooltipContext.hide();
  }, []);

  useEffect(() => {
    if (showCoverage && !isLoadingMSAData && !msaData?.length) {
      setIsLoadingMSAData(true);
      getServiceRegionsDetailed(ServiceTypeEnum.Inspector, msaData => {
        setMsaData(msaData);
        setIsLoadingMSAData(false);
      }, mounted);
    }
  }, [showCoverage, isLoadingMSAData, msaData]);

  const getCountyCoverage = (countyId: number) => {
    // list of FRs that cover the entire county (by county directly or by covering an MSA that includes that county)
    let frsWithFullCountyCoverage = fieldReps.filter(fr =>
      fr.serviceRegions.some(coverage => {
        if (coverage.countyId) {
          return (coverage.countyId === countyId && coverage.travelRuleId === null);
        } else if (coverage.serviceRegionId) {
          let msa = msaData.find(msa => msa.id === coverage.serviceRegionId);
          return msa.countyList.some(c => (c.id === countyId) && coverage.travelRuleId === null);
        }
      }));

    const frsWithSecondaryCountyCoverage = fieldReps.filter(fr =>
      fr.serviceRegions.some(coverage => {
        if (coverage.countyId) {
          return ((coverage.countyId === countyId) && coverage.travelRuleId !== null);
        }
        else if (coverage.serviceRegionId) {
          let msa = msaData.find(msa => msa.id === coverage.serviceRegionId);
          return msa.countyList.some(c => (c.id === countyId) && coverage.travelRuleId !== null);
        }
      }));
      
    // list of postal codes in the county linked to a list of FRs that cover that postal code
    let zipCoverageMap = countyPostalLinks.filter(pc => pc.countyId === countyId).map(link => (
      {
        postalId: link.postalId,
        frsWithCoverage: fieldReps.filter(fr => fr.serviceRegions.some(c => c.postalId === link.postalId && c.travelRuleId === null)),
      }
    ));

    const zipSecondaryCoverageMap = countyPostalLinks.filter(pc => pc.countyId === countyId).map(link => (
      {
        postalId: link.postalId,
        frsWithCoverage: fieldReps.filter(fr => fr.serviceRegions.some(c => c.postalId === link.postalId && c.travelRuleId !== null)),
      }
    ));

    let coverageLevel: CoverageLevel = "noCoverage";

    if (frsWithFullCountyCoverage.length > 0 || zipCoverageMap.every(z => z.frsWithCoverage.length > 0)) {
      coverageLevel = "fullCoverage";
    }
    else if (frsWithSecondaryCountyCoverage.length > 0 || zipSecondaryCoverageMap.every(z => z.frsWithCoverage.length > 0)) {
      coverageLevel = "fullSecondaryCoverage";
    }
    else if (zipCoverageMap.some(z => z.frsWithCoverage.length > 0) || zipSecondaryCoverageMap.some(z => z.frsWithCoverage.length > 0)){
      coverageLevel = "partialCoverage";
    }

    return {
      coverageLevel,
      frsWithFullCountyCoverage,
      frsWithSecondaryCountyCoverage,
      zipCoverageMap,
      zipSecondaryCoverageMap
    };
  };

  // Lazy load counties and zips for selected states
  useEffect(() => {
    if (showCoverage && !isLoadingCountyData && !isLoadingZipData && !isLoadingCountyPostalData) {
      const statesToLoad = selectedStates.filter(state => !loadedStates.has(state));
      
      if (statesToLoad.length) {
        setIsLoadingCountyData(true);
        getCounties({ stateAbbrevation: statesToLoad, includeBoundaries: true }, newCounties => {
          setCountyData(existing => [...existing, ...newCounties.filter(c => c.boundary)]);
          setLoadedStates(existing => combineSets(existing, statesToLoad));
          setIsLoadingCountyData(false);
        },
        mounted);

        setIsLoadingZipData(true);
        getZipCodes({ stateAbbrevation: statesToLoad, includeBoundaries: true }, newZips => {
          setZipData(existing => [...existing, ...newZips.filter(z => z.boundary)]);
          setIsLoadingZipData(false);
        },
        mounted);

        setIsLoadingCountyPostalData(true);
        getCountyPostalCodes({ stateAbbrevation: statesToLoad, includeBoundaries: false }, newCPC => {
          setCountyPostalLinks(existing => [...existing, ...newCPC]);
          setIsLoadingCountyPostalData(false);
        },
        mounted);
      }
    }
  }, [showCoverage, selectedStates, isLoadingCountyData, isLoadingZipData, isLoadingCountyPostalData]);

  const getFRName = (name: string) => name.replace(/\(.*?\)\s*/, "").trim();

  const getFrCoverageMouseOver = (
    mouseOverText: ReactNode,
    countyFRList: FieldRepCoverageData[],
    zipFRList: FieldRepCoverageData[] = null,
    frsWithSecondaryCountyCoverage: FieldRepCoverageData[] = null,
    frsWithSecondaryZipCoverage: FieldRepCoverageData[] = null) => {

    let showTwoColumns = (countyFRList?.length ?? 0) + (zipFRList?.length ?? 0) > 20;

    const sortFrs = (frList: FieldRepCoverageData[]) => frList.sort((fr1, fr2) => getFRName(fr1.name).localeCompare(getFRName(fr2.name)));

    const getFrDisplay = (frList: FieldRepCoverageData[]) => {
      if (!frList?.length) return <div>No coverage</div>; // needs to be in a div for some new line formatting

      const frMap = (fr: FieldRepCoverageData) => <div>{`${fr.name}${fr.isSecondary ? "*" : ""}`}</div>;
      let sortedFrs = sortFrs(frList);

      if (showTwoColumns) {
        let secondHalf = sortedFrs.splice(Math.ceil(sortedFrs.length / 2));

        return <div className="fr-tooltip-two-cols">
          <div>{sortedFrs.map(frMap)}</div>
          <div>{secondHalf.map(frMap)}</div>
        </div>;
      } else {
        return sortedFrs.map(frMap);
      }
    };

    let frCountyCoverage = (
      <>
        FR County Coverage:<br />
        {getFrDisplay([...countyFRList, ...frsWithSecondaryCountyCoverage.map(fr => ({ ...fr, isSecondary: true }))])}
      </>
    );

    let frZipCoverage = (
      <>
        FR Zip Coverage:<br />
        {getFrDisplay([...zipFRList, ...frsWithSecondaryZipCoverage.map(fr => ({ ...fr, isSecondary: true }))])}
        <br />
        * Indicates secondary coverage
      </>
    );

    return (
      <MCard title={mouseOverText}>
        {frCountyCoverage}
        <br />
        {frZipCoverage}
      </MCard>
    );
  };

  // updated our cached polygons based on new zip or county data being loaded
  useEffect(() => {
    if (!isLoadingCountyData && !isLoadingZipData && !isLoadingCountyPostalData && !isLoadingMSAData && msaData?.length && countyData?.length && zipData?.length && !!fieldReps && countyPostalLinks?.length) {
      let polys = mapArrayOfPolygonsV2<PolyMeta>(countyData.map(c => {
        let { coverageLevel, frsWithFullCountyCoverage, zipCoverageMap, frsWithSecondaryCountyCoverage, zipSecondaryCoverageMap } = getCountyCoverage(c.id);

        let msa = msaData?.find(msa => msa.countyList.some(mc => mc.id === c.id));

        // get unique list of all FRs that have at least some coverage of the county
        let frsWithPartialZipCoverage = zipCoverageMap.reduce<FieldRepCoverageData[]>((acc, frc) => {
          frc.frsWithCoverage.forEach(fr => {
            if (!acc.some(fr2 => fr2.id === fr.id)) acc.push(fr);
          });

          return acc;
        }, []);

        let frsWithSecondaryPartialZipCoverage = zipSecondaryCoverageMap.reduce<FieldRepCoverageData[]>((acc, frc) => {
          frc.frsWithCoverage.forEach(fr => {
            if (!acc.some(fr2 => fr2.id === fr.id)) acc.push(fr);
          });

          return acc;
        }, []);

        // if show zip coverage is on, we want empty partially covered counties so we can see the zip coverage more clearly
        if (coverageLevel === "partialCoverage" && showZipCoverage) coverageLevel = "noCoverage";

        let strokeWeight = mapColors.county[coverageLevel].borderSize;
        if (showZipCoverage) strokeWeight = countyBorderSizeZipCoverageOverride;

        let mouseOver = getFrCoverageMouseOver(`${c.name}, ${c.state} (${msa.name})`, frsWithFullCountyCoverage, frsWithPartialZipCoverage, frsWithSecondaryCountyCoverage, frsWithSecondaryPartialZipCoverage);

        return {
          id: `county-${c.id}`,
          pathJson: c.boundary,
          zIndex: 1,
          style: {
            fillColor: mapColors.county[coverageLevel].fillColor,
            fillOpacity: mapColors.county[coverageLevel].fillOpacity,
            strokeColor: mapColors.county[coverageLevel].borderColor,
            strokeWeight: strokeWeight,
          },
          meta: {
            polyType: BoundaryType.County,
            state: c.state,
            mouseOver: mouseOver,
          },
        };
      }));

      if (showZipCoverage) {
        polys.push(...mapArrayOfPolygonsV2<PolyMeta>(zipData.map(z => {
          const frsWithZipCoverage = fieldReps.filter(fr => fr.serviceRegions.some(r => r.postalId === z.id && r.travelRuleId === null));
          const frsWithSecondaryZipCoverage = fieldReps.filter(fr => fr.serviceRegions.some(r => r.postalId === z.id && r.travelRuleId !== null));

          const { frsWithFullCountyCoverage, frsWithSecondaryCountyCoverage } = getCountyCoverage(z.countyId);

          const mouseOver = getFrCoverageMouseOver(`${z.zipCode}, ${z.stateAbbreviation} (${z.county} County)`, frsWithFullCountyCoverage, frsWithZipCoverage, frsWithSecondaryCountyCoverage, frsWithSecondaryZipCoverage);
          let coverageLevel: CoverageLevel = "noCoverage";
          // Always make primary coverage for zips red
          if (frsWithZipCoverage.length) {
            coverageLevel = "fullCoverage";
          }
          // If the county has full coverage, we don't want the zip to display the secondary coverage color since the county full coverage trumps that
          else if (frsWithSecondaryZipCoverage.length && !frsWithFullCountyCoverage?.length) {
            coverageLevel = "fullSecondaryCoverage";
          }
          
          return {
            id: `zip-${z.id}`,
            pathJson: z.boundary,
            zIndex: 1,
            style: {
              fillColor: mapColors.zip[coverageLevel].fillColor,
              fillOpacity: mapColors.zip[coverageLevel].fillOpacity,
              strokeColor: mapColors.zip[coverageLevel].borderColor,
              strokeWeight: mapColors.zip[coverageLevel].borderSize,
            },
            meta: {
              polyType: BoundaryType.Zip,
              state: z.stateAbbreviation,
              mouseOver: mouseOver,
            } as PolyMeta,
          };
        })));
      }

      setCachedBoundaryPolys(polys);
    }
  
  }, [zipData, countyData, msaData, fieldReps, isLoadingZipData, isLoadingCountyData, isLoadingCountyPostalData, isLoadingMSAData, countyPostalLinks, showZipCoverage, forceUpdate]);

  // update the visble boundaries based state filter and FR data passed in
  useEffect(() => {
    if (!showCoverage) {
      setVisibleBoundaryPolys([]);
    } else if (cachedBoundaryPolys?.length && !!fieldReps) {
      const boundaryOrder = [BoundaryType.County, BoundaryType.Zip];

      let visibleBoundaries = cachedBoundaryPolys.filter(cached => selectedStates.includes(cached.meta.state));

      let newVisible = visibleBoundaries.map(p => {
        let newPoly = { ...p };
        newPoly.zIndex = boundaryOrder.findIndex(b => b === p.meta.polyType);

        if (showTooltips) {
          newPoly.onMouseOver = () => tooltipContext.show(p.meta.mouseOver, null, "fr-coverage-info");
          newPoly.onMouseOut = () => tooltipContext.hide();
        } else {
          // if you dont provide empty functions the google maps control breaks and wont let you pan... weird. wonder if we are doing something wrong elsewhere
          newPoly.onMouseOver = () => {};
          newPoly.onMouseOut = () => {};
        }

        return newPoly;
      });
      
      setVisibleBoundaryPolys(newVisible);
    }
  }, [cachedBoundaryPolys, selectedStates, fieldReps, showCoverage, showTooltips]);

  return {
    coveragePolygons: visibleBoundaryPolys,
    isLoading: {
      value: isLoadingCountyData || isLoadingZipData || isLoadingMSAData || isLoadingCountyPostalData,
      isLoadingCountyData,
      isLoadingZipData,
      isLoadingMSAData,
      isLoadingCountyPostalData,
    },
  };
};

export default useFRCoverage;
